import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Typography from "@mui/material/Typography";
import AppDialog from "../../shared/ui/AppDialog";
import { BRAND } from "../../shared/ui/theme";
import { usePreferences } from "./PreferencesProvider";
import { Preferences, sanitizePartial } from "./types";

type GroupKey = "menuStyle" | "dialogStyle" | "conferenceDetail";
type Option = { value: string; label: string; hint: string };

export const APPEARANCE_GROUPS: { key: GroupKey; title: string; options: Option[] }[] = [
  {
    key: "menuStyle",
    title: "Menu",
    options: [
      { value: "navy", label: "Navy", hint: "Padrão — menu escuro com o logo branco" },
      { value: "light", label: "Claro", hint: "Menu branco; a marca fica no logo" },
    ],
  },
  {
    key: "dialogStyle",
    title: "Modais",
    options: [
      { value: "classic", label: "Clássico", hint: "Padrão — cabeçalho e rodapé separados por linhas" },
      { value: "light", label: "Leve", hint: "Sem divisórias; contexto numa etiqueta" },
    ],
  },
  {
    key: "conferenceDetail",
    title: "Detalhe da Conferência",
    options: [
      { value: "table", label: "Tabela", hint: "Padrão — a tabela de conferência de hoje" },
      { value: "cards", label: "Cartões", hint: "Plantões em cartões compactos, sem rolagem lateral" },
    ],
  },
];

function Preview({ group, value }: { group: GroupKey; value: string }) {
  const frame = { width: 72, height: 44, borderRadius: "6px", border: `1px solid ${BRAND.divider}`, overflow: "hidden", display: "flex", flexShrink: 0, bgcolor: "#fff" };
  if (group === "menuStyle") {
    return (
      <Box aria-hidden sx={frame}>
        <Box sx={{ width: 18, bgcolor: value === "navy" ? BRAND.navy : "#fff", borderRight: value === "navy" ? "none" : `1px solid ${BRAND.divider}` }} />
        <Box sx={{ flex: 1, bgcolor: BRAND.background }} />
      </Box>
    );
  }
  if (group === "dialogStyle") {
    const classic = value === "classic";
    return (
      <Box aria-hidden sx={{ ...frame, flexDirection: "column" }}>
        <Box sx={{ height: 12, borderBottom: classic ? `1px solid ${BRAND.divider}` : "none", m: "3px 6px 0", bgcolor: classic ? "transparent" : "#eef6f9", borderRadius: classic ? 0 : "6px" }} />
        <Box sx={{ flex: 1 }} />
        <Box sx={{ height: 10, bgcolor: classic ? BRAND.background : "transparent", borderTop: classic ? `1px solid ${BRAND.divider}` : "none" }} />
      </Box>
    );
  }
  return (
    <Box aria-hidden sx={{ ...frame, p: "4px", gap: "3px", flexWrap: "wrap", alignContent: "flex-start" }}>
      {value === "table"
        ? [0, 1, 2, 3].map((i) => <Box key={i} sx={{ width: "100%", height: 6, bgcolor: i ? "#eef0f3" : "#dfe5ea" }} />)
        : [0, 1, 2, 3].map((i) => <Box key={i} sx={{ width: 28, height: 15, border: `1px solid ${BRAND.divider}`, borderRadius: "3px" }} />)}
    </Box>
  );
}

export default function AppearanceDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { prefs, setPref } = usePreferences();
  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title="Aparência"
      context="Menu, modais e detalhe da Conferência"
      size="md"
      footerLeft={<Typography sx={{ fontSize: 12, color: "text.secondary" }}>Suas escolhas ficam salvas na sua conta.</Typography>}
      actions={<Button variant="contained" onClick={onClose}>Fechar</Button>}
    >
      {APPEARANCE_GROUPS.map((group) => (
        <Box key={group.key} sx={{ mb: 2.5 }}>
          <Typography id={`appearance-${group.key}`} sx={{ fontWeight: 600, mb: 1 }}>{group.title}</Typography>
          <RadioGroup
            aria-labelledby={`appearance-${group.key}`}
            value={prefs[group.key]}
            onChange={(e) => {
              // Só valor conhecido vira preferência (o BE recusaria o resto com 422).
              const valid = sanitizePartial({ [group.key]: e.target.value });
              if (group.key in valid) setPref(group.key, valid[group.key] as Preferences[typeof group.key]);
            }}
            sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1 }}
          >
            {group.options.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio size="small" />}
                sx={{ m: 0, p: 1, gap: 1, alignItems: "center", border: "1px solid", borderRadius: 2, borderColor: prefs[group.key] === option.value ? "primary.main" : BRAND.divider }}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                    <Preview group={group.key} value={option.value} />
                    <Box>
                      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{option.label}</Typography>
                      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{option.hint}</Typography>
                    </Box>
                  </Box>
                }
              />
            ))}
          </RadioGroup>
        </Box>
      ))}
    </AppDialog>
  );
}
